import { Component } from 'react';
import { Tooltip } from '@mui/material';
import JxLineEdit from '../JxEdits/JxLineEdit';
import JxCheckbox from '../JxToggles/JxCheckbox';
import { tooltipSxElevated } from '../../components/shared/jernyTooltipStyling';
import JxIconButton from '../JxButtons/JxIconButton';
import JxSelect from '../JxSelect/JxSelect';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import JxFormValidationBase from './JxFormValidationBase';

import './JxFormFieldsBase.css';

/**
 * JxFormFieldsBase — shared FieldRow/Section rendering for JxForm editor bodies.
 * Extend this instead of copy-pasting FieldRow into every *EditorBody.jsx.
 *
 * Subclass provides:
 * - this.fieldDescriptions — object, used as tooltip text on field labels
 * - props.inputs / props.errors / props.onChange — same contract as before
 *
 * Tooltips: MUI Tooltip + tooltipSxElevated (components/shared/jernyTooltipStyling.js) —
 * standaard Jerny-tooltip-look, niet JxTooltip (deprecated 2026-07-08, zie
 * Development.Patterns rk=ed2d2d36-15de-4867-8b09-552a7801b626 en Jerny Source JxControls
 * rk=fe28abeb0f5844e8b19710aa268bb945).
 *
 * @param {object} inputs - field values, lowerCamelCase keys
 * @param {object} errors
 * @param {(field: string, value: any) => void} onChange
 */
export default class JxFormFieldsBase extends JxFormValidationBase {
    fieldDescriptions = {};

    state = { copiedField: null };

    constructor(props) {
        super(props);
        this.name = 'JxFormFieldsBase'; // required by JxForm
    }

    handleCopy = (field, value) => {
        navigator.clipboard.writeText(String(value ?? ''));
        this.setState({ copiedField: field });
        setTimeout(() => this.setState({ copiedField: null }), 1500);
    };

    Section = ({ title }) => <div className="jx-form-section">{title}</div>;

    /**
     * FieldRow — one schema field. `control` picks the widget type, `readonly` is an
     * orthogonal flag that disables whichever widget is rendered, `copyable` adds a
     * copy-to-clipboard button. For control="text" the copy button renders as a `suffix`
     * INSIDE the JxLineEdit box (see JxLineEdit.jsx) — not a separate wrapper div.
     * control="select" uses JxSelect (MUI Autocomplete wrapper, see JxSelect.jsx) — a
     * native <select>'s dropdown popup is browser-rendered and can't be themed.
     * TODO: textarea still falls back to plain HTML — no JxTextArea yet.
     *
     * @param {string} label
     * @param {'text'|'textarea'|'checkbox'|'select'} control - widget type
     * @param {string} field - inputs/onChange key, e.g. 'name'
     * @param {Array<{label:string,value:any}>} [options] - required when control="select"
     * @param {boolean} [readonly] - disables the widget regardless of control type
     * @param {boolean} [copyable] - shows a copy-to-clipboard icon button (text fields only, via suffix)
     * @param {string} [info] - tooltip text shown via a small info icon next to the label
     * @param {boolean} [clearToZero] - shows an X icon button (text fields only, via suffix) that resets the value to '0'
     * @param {'default'|'inline-right'|'top'} [layout] - label position:
     *   default: label 90px left of control (grid).
     *   inline-right: label+control right-aligned on one row (e.g. checkboxes like "Show Completed").
     *   top: label above control, full width (stacked).
     */
    FieldRow = ({ label, control, field, options, readonly = false, copyable = false, info, clearToZero = false, layout = 'default', maxLength, onEnter }) => {
        const { inputs, errors, onChange } = this.props;

        const id = `${this.getId()}_${field}`;
        const value = inputs[field];
        const descKey = field.charAt(0).toUpperCase() + field.slice(1); // 'group0' -> 'Group0'
        const desc = this.fieldDescriptions[descKey];

        const rowModifier =
            layout === 'inline-right' ? 'jx-form-row--inline-right' :
            layout === 'top'          ? 'jx-form-row--top' :
            readonly                  ? 'jx-form-row--readonly' : '';
        const rowClass = ['jx-form-row', rowModifier].filter(Boolean).join(' ');

        const copyButton = copyable && (
            <Tooltip title={this.state.copiedField === field ? 'Copied!' : 'Copy'} slotProps={tooltipSxElevated}>
                <JxIconButton icon={<ContentCopyIcon sx={{ fontSize: 14 }} />} onClick={() => this.handleCopy(field, value)} />
            </Tooltip>
        );

        const clearButton = clearToZero && (
            <Tooltip title="Reset to 0" slotProps={tooltipSxElevated}>
                <JxIconButton icon={<CloseIcon sx={{ fontSize: 13 }} />} onClick={() => onChange(field, '0')} />
            </Tooltip>
        );

        const infoIcon = info && (
            <Tooltip title={info} placement="left" slotProps={tooltipSxElevated}>
                <InfoOutlinedIcon className="jx-form-info-icon" sx={{ fontSize: 14 }} />
            </Tooltip>
        );

        const suffix = (infoIcon || clearButton || copyButton) && (
            <span className="jx-form-suffix-group">
                {infoIcon}
                {clearButton}
                {copyButton}
            </span>
        );

        return (
            <div className={rowClass}>
                <Tooltip title={desc ?? ''} placement="right" slotProps={tooltipSxElevated}>
                    <label className="jx-form-label">{label}</label>
                </Tooltip>

                {control === 'text' && (
                    <JxLineEdit
                        id={id}
                        value={value}
                        onChange={(v) => onChange(field, v)}
                        onEnter={onEnter}
                        error={errors?.[field]}
                        disabled={readonly}
                        maxLength={maxLength}
                        suffix={suffix}
                    />
                )}
                {control === 'checkbox' && (
                    <JxCheckbox id={id} checked={!!value} onChange={(v) => onChange(field, v)} disabled={readonly} />
                )}
                {control === 'textarea' && (
                    <textarea
                        id={id}
                        className="jx-line-edit"
                        value={value ?? ''}
                        onChange={(e) => onChange(field, e.target.value)}
                        disabled={readonly}
                    />
                )}
                {control === 'select' && (
                    <JxSelect id={id} value={value} onChange={(v) => onChange(field, v)} options={options} disabled={readonly} />
                )}
            </div>
        );
    };
}
